import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const { user, logoutUser } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setMenuOpen(false), [location]);

  const isActive = (path) => location.pathname === path;

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/job-list?q=${search}`);
  };

  return (
    <>
      {/* Main Navbar */}
      <nav className={`main-navbar ${scrolled ? "navbar-scrolled" : ""}`}>
        <div className="container-fluid px-4">
          <div className="d-flex align-items-center justify-content-between w-100 gap-3">
            {/* Logo */}
            <Link to="/" className="navbar-logo">
              <img
                src="/justhiringlogo.png"
                alt="JustHiring24"
                style={{
                  height: 80,
                  width: "auto",
                  objectFit: "contain",
                  borderRadius: "50%",
                }}
              />
              <span className="footer-logo-text">
                <span className="just">Just</span>
                <span className="accent">Hiring</span>
                <span className="accent">24</span>
              </span>
            </Link>

            {/* Search Bar - desktop */}
            <form
              onSubmit={handleSearch}
              className="navbar-search d-none d-lg-flex"
            >
              <div className="search-input-wrap">
                <i className="fa fa-search search-icon"></i>
                <input
                  type="text"
                  placeholder="Job title, keywords..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <div className="search-divider"></div>
              <div className="search-input-wrap">
                <i className="fa fa-map-marker-alt search-icon"></i>
                <input type="text" placeholder="City or remote" />
              </div>
              <button type="submit" className="search-btn">
                <i className="fa fa-search me-2"></i> Search
              </button>
            </form>

            {/* Right Actions */}
            <div className="d-flex align-items-center gap-2">
              <div className="nav-links d-none d-lg-flex align-items-center gap-1">
                <Link
                  to="/"
                  className={`nav-pill ${isActive("/") ? "active" : ""}`}
                >
                  Home
                </Link>
                <Link
                  to="/about"
                  className={`nav-pill ${isActive("/about") ? "active" : ""}`}
                >
                  About
                </Link>

                <div className="nav-dropdown-wrap">
                  <button className="nav-pill dropdown-trigger">
                    Jobs <i className="fa fa-chevron-down ms-1 small"></i>
                  </button>
                  <div className="nav-dropdown">
                    <Link to="/job-list" className="nav-dropdown-item">
                      <i className="fa fa-list-ul me-2 text-primary"></i> Job
                      List
                    </Link>
                    <Link to="/category" className="nav-dropdown-item">
                      <i className="fa fa-th-large me-2 text-primary"></i>{" "}
                      Categories
                    </Link>
                  </div>
                </div>

                <Link
                  to="/testimonial"
                  className={`nav-pill ${isActive("/testimonial") ? "active" : ""}`}
                >
                  Testimonials
                </Link>
                <Link
                  to="/pricing"
                  className={`nav-pill ${isActive("/pricing") ? "active" : ""}`}
                >
                  Pricing
                </Link>
                <Link
                  to="/contact"
                  className={`nav-pill ${isActive("/contact") ? "active" : ""}`}
                >
                  Contact Us
                </Link>
              </div>

              <div className="d-flex align-items-center gap-2 ms-2">
                {user ? (
                  <div className="nav-dropdown-wrap">
                    <button className="nav-pill dropdown-trigger d-flex align-items-center gap-2">
                      <div className="user-avatar">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <span>{user.name?.split(" ")[0]}</span>
                      <i className="fa fa-chevron-down ms-1 small"></i>
                    </button>
                    <div className="nav-dropdown">
                      <Link to="/profile" className="nav-dropdown-item">
                        <i className="fa fa-user me-2 text-primary"></i>My
                        Profile
                      </Link>
                      <Link to="/my-applications" className="nav-dropdown-item">
                        <i className="fa fa-file-alt me-2 text-primary"></i>My
                        Applications
                      </Link>
                      <Link to="/saved-jobs" className="nav-dropdown-item">
                        <i className="fa fa-heart me-2 text-primary"></i>Saved
                        Jobs
                      </Link>
                      <div
                        style={{
                          borderTop: "1px solid #f0f0f0",
                          margin: "6px 0",
                        }}
                      ></div>
                      <button
                        onClick={logoutUser}
                        className="nav-dropdown-item w-100 text-start"
                        style={{ color: "#ef4444" }}
                      >
                        <i className="fa fa-sign-out-alt me-2"></i>Logout
                      </button>
                    </div>
                  </div>
                ) : (
                  <Link to="/login" className="btn-signin d-none d-lg-flex">
                    <i className="fa fa-user me-2"></i> Login
                  </Link>
                )}
                <Link to="/post-job" className="btn-post">
                  <i className="fa fa-plus me-2"></i> Post a Job
                </Link>
              </div>

              {/* Mobile Toggle */}
              <button
                className="mobile-toggle d-lg-none"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <i className={`fa ${menuOpen ? "fa-times" : "fa-bars"}`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {menuOpen && (
            <div className="mobile-menu">
              <form onSubmit={handleSearch} className="mobile-search">
                <i className="fa fa-search"></i>
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <button type="submit">Search</button>
              </form>
              <Link
                to="/"
                className={`mobile-link ${isActive("/") ? "active" : ""}`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`mobile-link ${isActive("/about") ? "active" : ""}`}
              >
                About
              </Link>
              <Link
                to="/job-list"
                className={`mobile-link ${isActive("/job-list") ? "active" : ""}`}
              >
                Job List
              </Link>
              <Link
                to="/category"
                className={`mobile-link ${isActive("/category") ? "active" : ""}`}
              >
                Categories
              </Link>
              <Link
                to="/testimonial"
                className={`mobile-link ${isActive("/testimonial") ? "active" : ""}`}
              >
                Testimonial
              </Link>
              <Link
                to="/pricing"
                className={`mobile-link ${isActive("/pricing") ? "active" : ""}`}
              >
                Pricing
              </Link>
              <Link
                to="/contact"
                className={`mobile-link ${isActive("/contact") ? "active" : ""}`}
              >
                Contact
              </Link>
              <div className="d-flex gap-2 mt-3">
                {user ? (
                  <>
                    <Link to="/profile" className="mobile-link">
                      <i className="fa fa-user me-2"></i>My Profile
                    </Link>
                    <Link to="/my-applications" className="mobile-link">
                      <i className="fa fa-file-alt me-2"></i>My Applications
                    </Link>
                    <Link to="/saved-jobs" className="mobile-link">
                      <i className="fa fa-heart me-2"></i>Saved Jobs
                    </Link>
                    <button
                      onClick={logoutUser}
                      className="mobile-link text-start"
                      style={{
                        color: "#ef4444",
                        background: "none",
                        border: "none",
                      }}
                    >
                      <i className="fa fa-sign-out-alt me-2"></i>Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    className="btn-signin w-50 justify-content-center"
                  >
                    Sign In
                  </Link>
                )}
                <Link
                  to="/post-job"
                  className="btn-post w-50 justify-content-center"
                >
                  Post a Job
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}
